import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { Add, AddPhotoAlternate, Info, InsertDriveFile } from "@mui/icons-material";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import NullCheck from "../NullCheck";
import { useDispatch } from "react-redux";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import CustomIconButton from "components/CustomIconButton";
import { compressImageFile } from "config/functions";
import CustomCard from "components/CustomCard";

const MediaInput = ({ setCb, src, title, errors, clearErrors, name, fileName, disabled, allowedFormats, isGallery, showLoading }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { bt } = useBizTypeTranslation();
  const inputRef = useRef(null);
  const [media, setMedia] = useState(null);
  const [openMassge, setOpenMassge] = useState(false);

  const error = errors ? errors[name]?.type : false;
  const massge = errors ? errors[name]?.message : "";
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
  const MAX_OTHER_SIZE = 10 * 1024 * 1024;

  useEffect(() => {
    if (src) setMedia({ url: src, fileType: "image" })
  }, [src]);

  const isValidFileFormat = (file) => {
    if (!file) return false;
    if (!allowedFormats || allowedFormats?.length === 0) return false
    const fileType = file.type.toLowerCase();
    return allowedFormats.some(format => fileType.includes(format.toLowerCase()));
  };

  const onUpload = async (event) => {
    let file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!isValidFileFormat(file)) {
      return dispatch(
        setShowMassage({ massage: "MEDIA_NOT_ALLOWED_ERROR", type: "error" })
      );
    }

    const mimeType = file.type || "";
    const isImage = mimeType.startsWith("image/");
    const isVideo = mimeType.startsWith("video/");
    const isPdf = mimeType === "application/pdf";

    const maxSize = isImage
      ? MAX_IMAGE_SIZE
      : isVideo
        ? MAX_VIDEO_SIZE
        : MAX_OTHER_SIZE;

    if (file.size > maxSize) {
      return dispatch(
        setShowMassage({ massage: "MEDIA_SIZE_BIG_ERROR", type: "error" })
      );
    }

    if (showLoading) dispatch(setShowLoading(true));

    try {
      setMedia({
        url: URL.createObjectURL(file),
        fileType: isImage ? "image" : isVideo ? "video" : isPdf ? "pdf" : null,
      });

      if (fileName) {
        const ext = file.name.split(".").pop();
        const finalName = fileName.includes(".") ? fileName : `${fileName}.${ext}`;

        file = new File([file], finalName, {
          type: file.type,
          lastModified: file.lastModified,
        });
      }

      if (isImage) {
        const compressedFile = await compressImageFile(file);
        setCb(compressedFile);
        clearErrors(`${name}`);
      }
      else {
        setCb(file);
        clearErrors(`${name}`);
      }
    } catch (err) {
      console.error("onUpload error:", err);
      dispatch(
        setShowMassage({ massage: "MEDIA_UPLOAD_ERROR", type: "error" })
      );
    }
  };

  return (
    <Stack>
      <Typography fontSize={12}>{title ? `${bt(title)}:` : ""}</Typography>
      {isGallery
        ? <CustomIconButton
          icon={<Add sx={{ color: "primary.main" }} />}
          size={30}
          hideBgcolor
          disabled={disabled}
          onClick={() => inputRef.current.click()}
        />
        : <CustomCard
          width={{ xs: 150, md: 180 }}
          height={180}
          padding={1}
          justifyContent="space-between"
          sx={{ border: `2px solid ${theme.palette.primary.main}`, position: "relative" }}
        >

          <NullCheck valueForCheck={error === `${name}`}>
            <IconButton
              sx={{
                zIndex: 999,
                top: 0,
                left: 0,
                position: "absolute",
              }}
              onClick={() => setOpenMassge(true)}
            >
              <Info color="error" fontSize="small" />
            </IconButton>
          </NullCheck>

          {!media
            ? <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ width: "100%", height: "100%" }}
              onClick={() => inputRef.current.click()}
            >
              <AddPhotoAlternate sx={{ fontSize: 100, color: theme.palette.primary.main }} />
            </Stack>
            : media?.fileType === "image"
              ? <img
                src={media.url}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 10,
                  objectFit: "cover",
                  overflow: "hidden",
                }}
              />
              : media?.fileType === "video"
                ? <video
                  src={media.url}
                  autoPlay
                  loop
                  muted
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 10,
                    objectFit: "cover",
                    overflow: "hidden",
                  }}
                />
                : media?.fileType === "pdf"
                  ? <embed
                    src={media.url}
                    type="application/pdf"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 10,
                    }}
                  />
                  : <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{ width: "100%", height: "100%" }}
                  >
                    <InsertDriveFile sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  </Stack>
          }

          <CustomButton
            title="UPLOAD"
            fullWidth
            disabled={disabled}
            onClick={() => inputRef.current.click()}
          />
        </CustomCard>
      }

      <input
        ref={inputRef}
        style={{ display: "none" }}
        type="file"
        onChange={onUpload}
      />

      <NullCheck valueForCheck={massge}>
        <CustomDialog
          title="ERROR"
          open={openMassge}
          onClose={() => setOpenMassge(false)}
        >
          {massge}
        </CustomDialog>
      </NullCheck>
    </Stack>
  );
};

export default MediaInput;
